import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject, input,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { LoadingAndFeedbackState } from '../../models/loading-and-feedback-state.type';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { IconComponent } from '../icon/icon.component';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';

export type ButtonVariant = 'primary' | 'primary-white' | 'secondary' | 'tertiary' | 'tertiary-white' | 'inactive' | 'warning';
export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonFontWeight = 'medium' | 'regular';
export type ButtonSize = 'small' | 'default';

@Component({
  selector: 'work-ui-button',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    IconComponent,
    TranslateModule,
    LoadingSpinnerComponent,
    RouterLinkActive,
    TooltipDirective,
  ],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
})
export class ButtonComponent implements OnDestroy, OnChanges, AfterViewInit {
  @Input() active = false;
  @Input() badgeValue: number | string | undefined;
  @Input() alignCenter = false;
  @Input() alignRight = false;
  public alignColumn = input<boolean>(false)
  @Input() disabled = false;
  @Input() stopPropagation = false;
  @Input() fileDownloadName = '';
  @Input() floating = false;
  @Input() fontWeight: ButtonFontWeight = 'medium';
  @Input() fullWidth = false;
  @Input() fullWidthMobile = false;
  @Input() htmlLink = '';
  @Input() htmlLinkOpenNewTab = false;
  @Input() iconExternalSrc = '';
  @Input() iconName = '';
  @Input() iconOnly = false;
  @Input() iconPadding: number | undefined;
  @Input() iconPositionRight = false;
  @Input() linkStyling = false;
  @Input() loadingState: LoadingAndFeedbackState = 'none';
  @Input() maxWidth: number | undefined;
  @Input() queryParams: any;
  @Input() routerLink: any = undefined;
  @Input() showLoadingOnClick = false;
  @Input() size: ButtonSize = 'default';
  @Input() text = '';
  @Input() tooltipDescription = '';
  @Input() type: ButtonType = 'button';
  @Input() variant: ButtonVariant = 'primary';
  @Input() whiteBackground = false;
  @Input() width: number | undefined;
  public height = input<number | undefined>(undefined);
  @Output() loadingStateChange: EventEmitter<LoadingAndFeedbackState> = new EventEmitter<LoadingAndFeedbackState>();
  @Output() buttonClick = new EventEmitter();
  public buttonClicked = false;
  public initFinished = false;
  private elementRef = inject(ElementRef);

  get nativeElement() {
    return this.elementRef.nativeElement;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initFinished = true;
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loadingState'] && changes['loadingState'].currentValue) {
      switch (changes['loadingState'].currentValue as LoadingAndFeedbackState) {
        case 'loading': {
          this.showLoadingAnimation();
          break;
        }
        case 'success': {
          this.loadingStateChange.emit('success');
          break;
        }
        case 'error': {
          this.loadingStateChange.emit('error');
          break;
        }
        case 'none': {
          this.loadingStateChange.emit('none');
          break;
        }
      }
    }
  }

  ngOnDestroy() {
    try {
      this.loadingStateChange.unsubscribe();
      this.buttonClick.unsubscribe();
    } catch {
      // nothing to catch
    }
  }

  public async handleClick(event: any) {
    if (this.stopPropagation) {
      event.stopPropagation();
    }

    if (this.disabled  || this.loadingState === 'loading' ) {
      event.preventDefault();
      return;
    }

    this.buttonClick.emit(event);
    if (this.showLoadingOnClick) {
      this.showLoadingAnimation();
    }
  }

  /**
   * Show loading icon on click
   */
  private showLoadingAnimation() {
    this.loadingStateChange.emit('loading');
    this.loadingState = 'loading';
    this.buttonClicked = true;
  }
}
